import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { usePharmacy, BusinessHour, DoctorEntry, DoctorScheduleDay } from '@/context/PharmacyContext';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function formatTime(hhmm: string): string {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

function generateId(): string {
  return `doc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function PharmacySettingsPage() {
  const { settings, updateSettings, refresh } = usePharmacy();

  const [form, setForm] = useState({
    pharmacy_name: '',
    tagline: '',
    address_line1: '',
    address_line2: '',
    city: '',
    province: '',
    postal_code: '',
    country: '',
    phone: '',
    phone_raw: '',
    fax: '',
    email: '',
    latitude: '',
    longitude: '',
  });

  const [businessHours, setBusinessHours] = useState<BusinessHour[]>([]);
  const [doctors, setDoctors] = useState<DoctorEntry[]>([]);
  const [collapsedDoctors, setCollapsedDoctors] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'hours' | 'doctors'>('info');

  useEffect(() => {
    if (settings) {
      setForm({
        pharmacy_name: settings.pharmacy_name,
        tagline: settings.tagline,
        address_line1: settings.address_line1,
        address_line2: settings.address_line2,
        city: settings.city,
        province: settings.province,
        postal_code: settings.postal_code,
        country: settings.country,
        phone: settings.phone,
        phone_raw: settings.phone_raw,
        fax: settings.fax,
        email: settings.email,
        latitude: settings.latitude,
        longitude: settings.longitude,
      });
      setBusinessHours(settings.business_hours || []);
      setDoctors(settings.doctor_schedule || []);
    }
  }, [settings]);

  const updateForm = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateBusinessHour = (index: number, field: 'open' | 'close', value: string) => {
    setBusinessHours((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      if (updated.open && updated.close) {
        updated.hours = `${formatTime(updated.open)} – ${formatTime(updated.close)}`;
      }
      next[index] = updated;
      return next;
    });
  };

  // Doctor schedule helpers
  const addDoctor = () => {
    setDoctors((prev) => [
      ...prev,
      { id: generateId(), name: '', schedule: [] },
    ]);
  };

  const removeDoctor = (id: string) => {
    setDoctors((prev) => prev.filter((d) => d.id !== id));
  };

  const updateDoctorName = (id: string, name: string) => {
    setDoctors((prev) =>
      prev.map((d) => (d.id === id ? { ...d, name } : d)),
    );
  };

  const toggleDoctorDay = (doctorId: string, day: string) => {
    setDoctors((prev) =>
      prev.map((d) => {
        if (d.id !== doctorId) return d;
        const existing = d.schedule.find((s) => s.day === day);
        if (existing) {
          return { ...d, schedule: d.schedule.filter((s) => s.day !== day) };
        }
        return { ...d, schedule: [...d.schedule, { day, open: '08:00', close: '17:00' }] };
      }),
    );
  };

  const updateDoctorDayTime = (doctorId: string, day: string, field: 'open' | 'close', value: string) => {
    setDoctors((prev) =>
      prev.map((d) => {
        if (d.id !== doctorId) return d;
        return {
          ...d,
          schedule: d.schedule.map((s) => (s.day === day ? { ...s, [field]: value } : s)),
        };
      }),
    );
  };

  const toggleCollapse = (doctorId: string) => {
    setCollapsedDoctors((prev) => {
      const next = new Set(prev);
      if (next.has(doctorId)) {
        next.delete(doctorId);
      } else {
        next.add(doctorId);
      }
      return next;
    });
  };

  const handleSaveInfo = async () => {
    setSaving(true);
    setMessage(null);
    const result = await updateSettings(form);
    setSaving(false);
    if (result.success) {
      setMessage({ type: 'success', text: 'Pharmacy information saved!' });
      refresh();
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to save.' });
    }
  };

  const handleSaveHours = async () => {
    setSaving(true);
    setMessage(null);
    const result = await updateSettings({ business_hours: businessHours });
    setSaving(false);
    if (result.success) {
      setMessage({ type: 'success', text: 'Business hours saved!' });
      refresh();
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to save.' });
    }
  };

  const handleSaveDoctors = async () => {
    setSaving(true);
    setMessage(null);
    const result = await updateSettings({ doctor_schedule: doctors });
    setSaving(false);
    if (result.success) {
      setMessage({ type: 'success', text: 'Doctor schedule saved!' });
      refresh();
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to save.' });
    }
  };

  const inputClass = 'w-full px-3 py-2.5 rounded-lg border border-background-200 bg-background-50 focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm outline-none transition';
  const labelClass = 'block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1.5';
  const timeInputClass = 'px-2.5 py-2 rounded-lg border border-background-200 bg-background-50 focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm outline-none transition text-center w-28 font-mono cursor-pointer';

  const tabs = [
    { key: 'info' as const, label: 'Store Info', icon: 'ri-store-2-line' },
    { key: 'hours' as const, label: 'Business Hours', icon: 'ri-time-line' },
    { key: 'doctors' as const, label: 'Doctor Schedule', icon: 'ri-stethoscope-line' },
  ];

  return (
    <AdminLayout>
      <div className="p-6 md:p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground-950">Pharmacy Settings</h1>
          <p className="text-sm text-foreground-600 mt-1">Changes here reflect across the entire site immediately</p>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-accent-100 border border-accent-200'
          }`}>
            <i className={`${message.type === 'success' ? 'ri-check-line text-green-600' : 'ri-error-warning-line text-accent-600'} text-lg`}></i>
            <p className="text-sm font-medium">{message.text}</p>
          </div>
        )}

        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-6 w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-background-50 text-foreground-950 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className={`${tab.icon} text-base`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab: Store Info */}
        {activeTab === 'info' && (
          <div className="bg-background-50 border border-background-200 rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>Pharmacy Name</label>
                <input type="text" value={form.pharmacy_name} onChange={(e) => updateForm('pharmacy_name', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Tagline</label>
                <input type="text" value={form.tagline} onChange={(e) => updateForm('tagline', e.target.value)} className={inputClass} />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Address Line 1</label>
                <input type="text" value={form.address_line1} onChange={(e) => updateForm('address_line1', e.target.value)} className={inputClass} />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Address Line 2</label>
                <input type="text" value={form.address_line2} onChange={(e) => updateForm('address_line2', e.target.value)} className={inputClass} placeholder="Unit, Suite, etc." />
              </div>
              <div>
                <label className={labelClass}>City</label>
                <input type="text" value={form.city} onChange={(e) => updateForm('city', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Province</label>
                <input type="text" value={form.province} onChange={(e) => updateForm('province', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Postal Code</label>
                <input type="text" value={form.postal_code} onChange={(e) => updateForm('postal_code', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Country</label>
                <input type="text" value={form.country} onChange={(e) => updateForm('country', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Phone (Display)</label>
                <input type="text" value={form.phone} onChange={(e) => updateForm('phone', e.target.value)} className={inputClass} placeholder="(905) 709-9985" />
              </div>
              <div>
                <label className={labelClass}>Phone (Raw for tel: links)</label>
                <input type="text" value={form.phone_raw} onChange={(e) => updateForm('phone_raw', e.target.value)} className={inputClass} placeholder="9057099985" />
              </div>
              <div>
                <label className={labelClass}>Fax</label>
                <input type="text" value={form.fax} onChange={(e) => updateForm('fax', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input type="email" value={form.email} onChange={(e) => updateForm('email', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Latitude</label>
                <input type="text" value={form.latitude} onChange={(e) => updateForm('latitude', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Longitude</label>
                <input type="text" value={form.longitude} onChange={(e) => updateForm('longitude', e.target.value)} className={inputClass} />
              </div>
            </div>
            <div className="mt-6">
              <button
                onClick={handleSaveInfo}
                disabled={saving}
                className="bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {saving ? 'Saving...' : 'Save Information'}
              </button>
            </div>
          </div>
        )}

        {/* Tab: Business Hours */}
        {activeTab === 'hours' && (
          <div className="bg-background-50 border border-background-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-base font-bold text-foreground-950">Operating Hours</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Set open and close times for each day. Use 24-hour native time pickers.</p>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-background-200">
              {/* Header */}
              <div className="grid grid-cols-[1fr_140px_140px_1fr] gap-4 px-5 py-3 bg-background-50 border-b border-background-200">
                <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Day</span>
                <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider text-center">Open</span>
                <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider text-center">Close</span>
                <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Display</span>
              </div>

              <div className="divide-y divide-background-100">
                {businessHours.map((bh, index) => {
                  const displayText = bh.open && bh.close
                    ? `${formatTime(bh.open)} – ${formatTime(bh.close)}`
                    : bh.hours || 'Closed';

                  return (
                    <div key={bh.day} className="grid grid-cols-[1fr_140px_140px_1fr] gap-4 px-5 py-3.5 items-center bg-background-50">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${bh.open && bh.close ? 'bg-green-400' : 'bg-gray-300'}`}></div>
                        <span className="text-sm font-bold text-foreground-900">{bh.day}</span>
                      </div>

                      {/* Open time picker */}
                      <div className="flex justify-center">
                        <input
                          type="time"
                          value={bh.open || ''}
                          onChange={(e) => updateBusinessHour(index, 'open', e.target.value)}
                          className={timeInputClass}
                        />
                      </div>

                      {/* Close time picker */}
                      <div className="flex justify-center">
                        <input
                          type="time"
                          value={bh.close || ''}
                          onChange={(e) => updateBusinessHour(index, 'close', e.target.value)}
                          className={timeInputClass}
                        />
                      </div>

                      {/* Derived display */}
                      <span className="text-sm text-foreground-600 font-medium pl-2">
                        {displayText}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 flex items-center gap-4">
              <button
                onClick={handleSaveHours}
                disabled={saving}
                className="bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {saving ? 'Saving...' : 'Save Business Hours'}
              </button>
              <span className="text-xs text-foreground-400">
                Changes appear instantly on the homepage and location page.
              </span>
            </div>
          </div>
        )}

        {/* Tab: Doctor Schedule */}
        {activeTab === 'doctors' && (
          <div className="space-y-4 md:space-y-6">
            {/* Doctor Cards */}
            {doctors.map((doctor) => {
              const isCollapsed = collapsedDoctors.has(doctor.id);
              const activeDays = doctor.schedule
                .sort((a, b) => DAYS.indexOf(a.day) - DAYS.indexOf(b.day));

              return (
                <div key={doctor.id} className="bg-background-50 border border-background-200 rounded-xl overflow-hidden transition-shadow">
                  {/* Header — always visible, clickable to toggle */}
                  <button
                    onClick={() => toggleCollapse(doctor.id)}
                    className="w-full flex flex-col sm:flex-row sm:items-center gap-3 px-4 md:px-6 py-4 text-left hover:bg-background-50/60 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-3-line text-foreground-500 text-base md:text-lg"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        {doctor.name ? (
                          <span className="text-sm md:text-base font-bold text-foreground-950 block truncate">{doctor.name}</span>
                        ) : (
                          <span className="text-sm md:text-base font-medium text-foreground-400 italic block truncate">Unnamed Doctor</span>
                        )}
                        {!isCollapsed && activeDays.length > 0 && (
                          <span className="text-[11px] text-foreground-500 mt-0.5 block">
                            {activeDays.length} day{activeDays.length !== 1 ? 's' : ''} • {activeDays[0]?.day.slice(0, 3)} {formatTime(activeDays[0]?.open)}–{formatTime(activeDays[0]?.close)}{activeDays.length > 1 ? ` +${activeDays.length - 1} more` : ''}
                          </span>
                        )}
                        {isCollapsed && activeDays.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {activeDays.map((s) => (
                              <span key={s.day} className="inline-flex items-center gap-1 text-[11px] bg-background-100 text-foreground-600 px-2 py-0.5 rounded-full">
                                <span className="font-semibold">{s.day.slice(0, 3)}</span>
                                <span className="text-foreground-400">{formatTime(s.open)}–{formatTime(s.close)}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); removeDoctor(doctor.id); }}
                        className="w-8 h-8 md:w-9 md:h-9 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center transition-colors cursor-pointer flex-shrink-0"
                        title="Remove doctor"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                      <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-foreground-400">
                        <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${isCollapsed ? '' : 'rotate-180'}`}></i>
                      </div>
                    </div>
                  </button>

                  {/* Collapsible content */}
                  <div
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
                    }`}
                  >
                    <div className="px-4 md:px-6 pb-5 border-t border-background-100">
                      {/* Doctor name input (inside expanded area) */}
                      <div className="pt-4 pb-3">
                        <label className="block text-[11px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">Doctor Name</label>
                        <input
                          type="text"
                          value={doctor.name}
                          onChange={(e) => updateDoctorName(doctor.id, e.target.value)}
                          placeholder="e.g. Dr. Smith"
                          className="w-full max-w-sm px-3 py-2 rounded-lg border border-background-200 bg-background-50 focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm font-semibold outline-none transition"
                        />
                      </div>

                      {/* Weekly schedule grid */}
                      <div className="overflow-hidden rounded-lg border border-background-200">
                        {/* Desktop header */}
                        <div className="hidden sm:grid grid-cols-[1fr_60px_95px_95px] md:grid-cols-[1fr_70px_110px_110px] gap-2 md:gap-3 px-3 md:px-4 py-2.5 bg-background-50 border-b border-background-200">
                          <span className="text-[10px] md:text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">Day</span>
                          <span className="text-[10px] md:text-[11px] font-semibold text-foreground-500 uppercase tracking-wider text-center">Works</span>
                          <span className="text-[10px] md:text-[11px] font-semibold text-foreground-500 uppercase tracking-wider text-center">Start</span>
                          <span className="text-[10px] md:text-[11px] font-semibold text-foreground-500 uppercase tracking-wider text-center">End</span>
                        </div>

                        <div className="divide-y divide-background-100">
                          {DAYS.map((day) => {
                            const daySchedule = doctor.schedule.find((s) => s.day === day);
                            const isActive = !!daySchedule;

                            return (
                              <div key={day}>
                                {/* Desktop row */}
                                <div className="hidden sm:grid grid-cols-[1fr_60px_95px_95px] md:grid-cols-[1fr_70px_110px_110px] gap-2 md:gap-3 px-3 md:px-4 py-2 items-center bg-background-50">
                                  <div className="flex items-center gap-2 md:gap-3">
                                    <button
                                      onClick={() => toggleDoctorDay(doctor.id, day)}
                                      className={`w-4 h-4 md:w-5 md:h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                                        isActive
                                          ? 'bg-primary-500 border-primary-500'
                                          : 'border-background-300 hover:border-background-400'
                                      }`}
                                    >
                                      {isActive && <i className="ri-check-line text-white text-[10px] md:text-xs"></i>}
                                    </button>
                                    <span className={`text-xs md:text-sm font-medium ${isActive ? 'text-foreground-900' : 'text-foreground-500'}`}>
                                      {day.slice(0, 3)}
                                    </span>
                                  </div>

                                  <div className="flex justify-center">
                                    <span className={`text-[10px] md:text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                                      isActive
                                        ? 'bg-green-50 text-green-700 border border-green-200'
                                        : 'bg-background-50 text-foreground-400 border border-background-200'
                                    }`}>
                                      {isActive ? 'Yes' : '—'}
                                    </span>
                                  </div>

                                  <div className="flex justify-center">
                                    <input
                                      type="time"
                                      value={daySchedule?.open || '08:00'}
                                      onChange={(e) => updateDoctorDayTime(doctor.id, day, 'open', e.target.value)}
                                      disabled={!isActive}
                                      className={`px-1.5 md:px-2 py-1 md:py-1.5 rounded-lg border text-xs md:text-sm text-center w-full max-w-[85px] md:max-w-[100px] font-mono outline-none transition cursor-pointer ${
                                        isActive
                                          ? 'border-background-200 bg-background-50 focus:ring-2 focus:ring-primary-400 focus:border-transparent'
                                          : 'border-background-100 bg-background-100 text-foreground-300 cursor-not-allowed'
                                      }`}
                                    />
                                  </div>

                                  <div className="flex justify-center">
                                    <input
                                      type="time"
                                      value={daySchedule?.close || '17:00'}
                                      onChange={(e) => updateDoctorDayTime(doctor.id, day, 'close', e.target.value)}
                                      disabled={!isActive}
                                      className={`px-1.5 md:px-2 py-1 md:py-1.5 rounded-lg border text-xs md:text-sm text-center w-full max-w-[85px] md:max-w-[100px] font-mono outline-none transition cursor-pointer ${
                                        isActive
                                          ? 'border-background-200 bg-background-50 focus:ring-2 focus:ring-primary-400 focus:border-transparent'
                                          : 'border-background-100 bg-background-100 text-foreground-300 cursor-not-allowed'
                                      }`}
                                    />
                                  </div>
                                </div>

                                {/* Mobile row */}
                                <div className="sm:hidden flex items-center gap-2 px-3 py-2.5">
                                  <button
                                    onClick={() => toggleDoctorDay(doctor.id, day)}
                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                                      isActive
                                        ? 'bg-primary-500 border-primary-500'
                                        : 'border-background-300 hover:border-background-400'
                                    }`}
                                  >
                                    {isActive && <i className="ri-check-line text-white text-[10px]"></i>}
                                  </button>
                                  <span className={`text-xs font-bold flex-shrink-0 w-10 ${isActive ? 'text-foreground-900' : 'text-foreground-400'}`}>
                                    {day.slice(0, 3)}
                                  </span>
                                  {isActive ? (
                                    <div className="flex items-center gap-1.5 flex-1">
                                      <input
                                        type="time"
                                        value={daySchedule?.open || '08:00'}
                                        onChange={(e) => updateDoctorDayTime(doctor.id, day, 'open', e.target.value)}
                                        className="px-1 py-1 rounded border border-background-200 bg-background-50 text-xs font-mono text-center w-20 outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                                      />
                                      <span className="text-foreground-300 text-xs">–</span>
                                      <input
                                        type="time"
                                        value={daySchedule?.close || '17:00'}
                                        onChange={(e) => updateDoctorDayTime(doctor.id, day, 'close', e.target.value)}
                                        className="px-1 py-1 rounded border border-background-200 bg-background-50 text-xs font-mono text-center w-20 outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                                      />
                                    </div>
                                  ) : (
                                    <span className="text-xs text-foreground-300 italic flex-1">Off</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add Doctor Button */}
            <button
              onClick={addDoctor}
              className="w-full border-2 border-dashed border-background-300 hover:border-primary-400 rounded-xl p-4 md:p-5 flex items-center justify-center gap-2 text-sm font-medium text-foreground-500 hover:text-primary-600 transition-all cursor-pointer bg-background-50 hover:bg-primary-50/30"
            >
              <i className="ri-add-line text-lg"></i>
              Add Doctor
            </button>

            {/* Save */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <button
                onClick={handleSaveDoctors}
                disabled={saving}
                className="bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {saving ? 'Saving...' : 'Save Doctor Schedule'}
              </button>
              <span className="text-xs text-foreground-400">
                Doctor schedule appears on the Location page.
              </span>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}